import type {
  CogoXyzListTemplatesInput,
  CogoXyzListTemplatesOutput,
  CogoXyzRemixTemplateInput,
  CogoXyzTemplateSummary,
} from '@cogo/types-v2'
import { FEATURED_TEMPLATES, type FeaturedTemplate } from '@/pages/homepage/data/featured-templates'
import { useStudioStore } from '@/store/studio-store'
import { openStudioLaunchChat } from '@/features/studio-dashboard/lib/studio-launch-chat'

export interface RemixedCogoXyzTemplate {
  template: CogoXyzTemplateSummary
  projectId: string
  projectName: string
  bundleRef: string
  previewUrl: string
  galleryCandidateUrl: string
}

function toTemplateSummary(template: FeaturedTemplate): CogoXyzTemplateSummary {
  return {
    id: template.id,
    label: template.label,
    description: template.description,
    category: template.category,
    keywords: template.keywords,
    blocks: template.blocks,
    route: template.route,
    thumbnail: template.thumbnail,
    v2Score: template.v2Score,
  }
}

function getOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin
  return 'https://cogo.xyz'
}

function matchesQuery(template: CogoXyzTemplateSummary, query: string): boolean {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return true
  return [
    template.id,
    template.label,
    template.description,
    template.category,
    ...template.keywords,
  ].some((value) => value.toLowerCase().includes(normalized))
}

export function listCogoXyzTemplates(input: CogoXyzListTemplatesInput = {}): CogoXyzListTemplatesOutput {
  const category = input.category?.trim()
  const query = input.query?.trim() ?? ''
  const limit = Math.max(1, input.limit ?? FEATURED_TEMPLATES.length)
  const templates = FEATURED_TEMPLATES
    .map(toTemplateSummary)
    .filter((template) => !category || category === 'all' || template.category === category)
    .filter((template) => matchesQuery(template, query))
    .slice(0, limit)

  return {
    templates,
    total: templates.length,
  }
}

export function getCogoXyzTemplate(templateId: string): CogoXyzTemplateSummary {
  const template = FEATURED_TEMPLATES.find((item) => item.id === templateId)
  if (!template) throw new Error(`Unknown Coogo template: ${templateId}`)
  return toTemplateSummary(template)
}

export async function remixCogoXyzTemplate(input: CogoXyzRemixTemplateInput): Promise<RemixedCogoXyzTemplate> {
  const template = getCogoXyzTemplate(input.templateId)
  const projectName = input.name?.trim() || `${template.label} Remix`
  const projectId = await useStudioStore.getState().createServerProject(projectName, input.templateId)
  openStudioLaunchChat({
    source: 'template-remix',
    projectName,
    templateName: template.label,
  })
  return {
    template,
    projectId,
    projectName,
    bundleRef: `studio://projects/${projectId}/templates/${template.id}`,
    previewUrl: `${getOrigin()}/ide/${projectId}`,
    galleryCandidateUrl: `${getOrigin()}/gallery/${projectId}`,
  }
}
